import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import isMobile from 'ismobilejs';
import Tooltip from 'react-tooltip';
import formatDate from 'date-fns/format';
import _ from 'underscore';

import TextMessage from './TextMessage';
import ImageMessage from './ImageMessage';
import FileMessage from './FileMessage';
import CarouselMessage from './CarouselMessage';
import Action from './Action';
import Avatar from './Avatar';
import LocationMessage from './LocationMessage';
import MessageStatusIndicator from './MessageStatusIndicator';
import FormMessage from './FormMessage';

import { getElementProperties } from '../utils/dom';
import { isSupportedMessageType, shouldIgnoreText } from '../utils/message';
import { setBlockChatInput } from '../actions/app-state';
import { resendMessage, sendMessage, setLatestAppUserMessageTimestamp } from '../actions/conversation';
import { IS_FIREFOX } from '../constants/browsers';
import { CAROUSEL_MESSAGE } from '../constants/dimensions';
import { SEND_STATUS, GLOBAL_ACTION_TYPES, MESSAGE_TYPES, ROLES } from '../constants/message';
import { PRECHAT_CAPTURE_ERROR } from '../constants/prechat-capture';
import { generateFormResponse, isCurrentUserMessage } from '../utils/message';
import { defaultAvatar } from '../constants/assets';

const getUpdatedMessageReadStatus = (props) => {
    const { appMakerLastRead, currentUser, received, participants } = props;

    const readTimestamps = _.compact(
        participants.filter((participant) => participant.appuserId !== currentUser._id).map((p) => p.lastRead),
    ).filter((timestamp) => timestamp >= received);

    if (appMakerLastRead >= received) {
        readTimestamps.push(appMakerLastRead);
    }

    const earliestReadTimestamp = readTimestamps.length > 0 ? Math.min(...readTimestamps) : 0;

    return {
        isMessageRead: !!earliestReadTimestamp,
        otherUsersReadTimestamp: earliestReadTimestamp,
    };
};

class MessageComponent extends React.PureComponent {
    static propTypes = {
        _clientId: PropTypes.number,
        _clientSent: PropTypes.number,
        _id: PropTypes.string,
        accentColor: PropTypes.string,
        actions: PropTypes.array,
        authorId: PropTypes.string,
        coordinates: PropTypes.object,
        currentUser: PropTypes.object.isRequired,
        submitPrechatCapture: PropTypes.func,
        avatarUrl: PropTypes.string,
        clickToRetryText: PropTypes.string.isRequired,
        clickToRetryFormText: PropTypes.string.isRequired,
        dateFnLocale: PropTypes.object.isRequired,
        dispatch: PropTypes.func.isRequired,
        fields: PropTypes.array,
        firstInGroup: PropTypes.bool,
        hasFormAfter: PropTypes.bool,
        hasFormBefore: PropTypes.bool,
        isChatInputBlocked: PropTypes.bool,
        isPrechatCaptureForm: PropTypes.bool,
        lastInGroup: PropTypes.bool,
        latestAppUserMessageTimestamp: PropTypes.number.isRequired,
        shouldShowAuthor: PropTypes.bool,
        shouldShowAvatar: PropTypes.bool,
        linkColor: PropTypes.string,
        displaySettings: PropTypes.object,
        locationSendingFailedText: PropTypes.string.isRequired,
        mediaUrl: PropTypes.string,
        mediaSize: PropTypes.number,
        messageTimestampFormat: PropTypes.string.isRequired,
        name: PropTypes.string,
        received: PropTypes.number,
        role: PropTypes.string.isRequired,
        sendStatus: PropTypes.string,
        showStatusIndicator: PropTypes.bool,
        submitted: PropTypes.bool,
        tapToRetryText: PropTypes.string.isRequired,
        tapToRetryFormText: PropTypes.string.isRequired,
        text: PropTypes.string,
        textFallback: PropTypes.string,
        type: PropTypes.string.isRequired,
        unsupportedMessageTypeText: PropTypes.string.isRequired,
        conversationId: PropTypes.string,
        messageRef: PropTypes.func.isRequired,
        appMakerLastRead: PropTypes.number,
        participants: PropTypes.array.isRequired,
    };

    static defaultProps = {
        actions: [],
        isChatInputBlocked: false,
        isPrechatCaptureForm: false,
        sendStatus: SEND_STATUS.SENT,
        showStatusIndicator: false,
    };

    state = {};

    static getDerivedStateFromProps = (props, prevState) => {
        const { mediaUrl, text, type, role, currentUser, authorId } = props;

        const actions = props.actions.filter(({ type }) => !GLOBAL_ACTION_TYPES.includes(type));
        const hasText = text && text.trim() && text.trim() !== mediaUrl;
        const hasFile = type === MESSAGE_TYPES.FILE;
        const hasImage = type === MESSAGE_TYPES.IMAGE;
        const hasCarousel = type === MESSAGE_TYPES.CAROUSEL || type === MESSAGE_TYPES.LIST;
        const hasLocation = type === MESSAGE_TYPES.LOCATION;
        const hasForm = type === MESSAGE_TYPES.FORM;
        const isCurrentUser = isCurrentUserMessage({ role, authorId }, currentUser._id);
        const hasActions = !hasCarousel && actions.length > 0;
        let lastItem;

        if (hasActions) {
            lastItem = 'actions';
        } else if (!hasLocation && (hasText || hasFile)) {
            lastItem = MESSAGE_TYPES.TEXT;
        } else if (hasLocation) {
            lastItem = MESSAGE_TYPES.LOCATION;
        }

        const isFormSubmitted = prevState.isFormSubmitted || !!props.submitted;
        const isFormFieldValuesAlreadyPopulated =
            prevState.formFieldValues && Object.keys(prevState.formFieldValues).length > 0;
        let formFieldValues;

        if (isFormFieldValuesAlreadyPopulated) {
            formFieldValues = prevState.formFieldValues;
        } else if (hasForm && isFormSubmitted) {
            formFieldValues = props.fields.reduce(
                (fieldValues, field) => ({
                    ...fieldValues,
                    [field.name]: field[field.type],
                }),
                {},
            );
        } else {
            formFieldValues = {};
        }

        return {
            formFieldValues,
            hasText,
            hasFile,
            hasImage,
            hasCarousel,
            hasLocation,
            hasActions,
            hasForm,
            hasFormSubmissionFailed: prevState.hasFormSubmissionFailed || false,
            isCurrentUser,
            isFormSubmitted,
            lastItem,
            hasImageFailure: false,
            ...getUpdatedMessageReadStatus(props),
        };
    };

    componentDidMount() {
        if (this.props.actions.length === 0) {
            this._restyleBubble();
        }

        this._updateLatestAppUserMessageTimestamp();

        const isLatestSuccessfulAppUserMessage =
            this.props.role === 'appUser' &&
            this.props.received >= this.props.latestAppUserMessageTimestamp &&
            this.props.sendStatus === SEND_STATUS.SENT;

        if (this.props.isChatInputBlocked && isLatestSuccessfulAppUserMessage) {
            this.props.dispatch(setBlockChatInput(false));
        }
    }

    _restyleBubble() {
        if (this.props.type !== MESSAGE_TYPES.TEXT) {
            return;
        }

        const bubble = this._messageContent;
        if (bubble) {
            const messageElement = bubble.firstChild;

            if (!messageElement) {
                return;
            }

            const messageProperties = getElementProperties(messageElement);
            const bubbleProperties = getElementProperties(bubble);

            if (!messageProperties || !bubbleProperties) {
                return;
            }

            const multiLineCheck = parseInt(bubbleProperties.fontSize) * 2;

            if (messageProperties.height > multiLineCheck && messageProperties.width < bubbleProperties.width) {
                bubble.style.width =
                    messageProperties.width +
                    parseInt(bubbleProperties.paddingLeft) +
                    parseInt(bubbleProperties.paddingRight) +
                    'px';
            }
        }
    }

    _getContainerClasses() {
        const { sendStatus } = this.props;
        const { hasActions, hasImage, hasCarousel, hasForm } = this.state;
        const containerClasses = ['msg'];

        if (hasImage || hasActions) {
            containerClasses.push('msg-image');
        }

        if (hasCarousel) {
            containerClasses.push('msg-carousel');
        }

        if (hasForm) {
            containerClasses.push('msg-form');

            if (this.state.isFormSubmitted) {
                containerClasses.push('form-submitted');
            }
        }

        if ([SEND_STATUS.SENDING, SEND_STATUS.FAILED].includes(sendStatus)) {
            containerClasses.push('msg-unsent');
        }

        return containerClasses.join(' ');
    }

    _getTextClasses() {
        const { hasImageFailure, lastItem } = this.state;
        const textClasses = ['message-item', 'message-text'];

        if (lastItem === 'text' || hasImageFailure) {
            textClasses.push('last-item');
        }

        return textClasses.join(' ');
    }

    _getRowClasses() {
        const { firstInGroup, lastInGroup } = this.props;
        const { isCurrentUser } = this.state;
        const rowClasses = ['row'];

        if (isCurrentUser) {
            rowClasses.push('right-row');
        } else {
            rowClasses.push('left-row');
        }

        if (this.props.type === MESSAGE_TYPES.FORM) {
            rowClasses.push('row-form');
        } else {
            if (firstInGroup) {
                if (isCurrentUser) {
                    rowClasses.push('row-current-user-first');
                } else {
                    rowClasses.push('row-other-user-first');

                    if (this.props.hasFormAfter) {
                        rowClasses.push('row-other-user-last');
                    }
                }
            }

            if (lastInGroup) {
                if (isCurrentUser) {
                    rowClasses.push('row-current-user-last');
                } else {
                    if (this.props.hasFormBefore) {
                        rowClasses.push('row-other-user-first');
                    }

                    rowClasses.push('row-other-user-last');
                }
            }

            if (!firstInGroup && !lastInGroup) {
                if (isCurrentUser) {
                    rowClasses.push('row-current-user-middle');
                } else if (!this.props.hasFormBefore && !this.props.hasFormAfter) {
                    rowClasses.push('row-other-user-middle');
                } else {
                    if (this.props.hasFormBefore) {
                        rowClasses.push('row-other-user-first');
                    }

                    if (this.props.hasFormAfter) {
                        rowClasses.push('row-other-user-last');
                    }
                }
            }
        }

        return rowClasses.join(' ');
    }

    _getActionClasses() {
        const { lastItem } = this.state;
        const actionListClasses = ['message-item'];

        if (lastItem === 'actions') {
            actionListClasses.push('last-item');
        }

        return actionListClasses.join(' ');
    }

    _getLocationClasses() {
        const { sendStatus } = this.props;
        const { lastItem } = this.state;
        const locationClasses = ['message-item', 'message-text'];

        if (lastItem === 'location') {
            locationClasses.push('last-item');
        }

        if (sendStatus === SEND_STATUS.SENDING) {
            locationClasses.push('message-location-loading');
        }

        return locationClasses.join(' ');
    }

    _updateLatestAppUserMessageTimestamp = () => {
        const { dispatch, latestAppUserMessageTimestamp, received, role } = this.props;
        const isMoreRecent = received > latestAppUserMessageTimestamp;
        const { conversationId } = this.props;
        if (role === ROLES.APP_USER && isMoreRecent) {
            dispatch(setLatestAppUserMessageTimestamp(conversationId, received));
        }
    };

    onFormSubmitted = () => {
        this.props.dispatch(setBlockChatInput(false));
        this.setState({ hasFormSubmissionFailed: false, isFormSubmitted: true });
    };

    onLoadImage = () => {
        this.setState({
            hasImage: true,
            hasImageFailure: false,
        });
    };

    onImageError = () => {
        this.setState({
            hasImage: false,
            hasImageFailure: true,
        });
    };

    onMessageClick() {
        const { sendStatus } = this.props;

        if (sendStatus === SEND_STATUS.FAILED) {
            this.props.dispatch(resendMessage(this.props._clientId));
        } else if (this.props.type === MESSAGE_TYPES.FORM && this.state.hasFormSubmissionFailed) {
            if (this.props.isPrechatCaptureForm) {
                this.submitForm();
            } else {
                const findFailedFormResponse = (m) =>
                    m.type === MESSAGE_TYPES.FORM_RESPONSE &&
                    m.sendStatus === SEND_STATUS.FAILED &&
                    m.quotedMessageId === this.props._id;
                this.props.dispatch(
                    resendMessage(this.props._clientId, {
                        failedMessagePredicate: findFailedFormResponse,
                        callbacks: { onSuccessCallback: this.onFormSubmitted },
                    }),
                );
            }
        }
    }

    setFormFieldValue = (name, value, callback) => {
        this.setState(
            {
                formFieldValues: {
                    ...this.state.formFieldValues,
                    [name]: value,
                },
            },
            callback,
        );
    };

    submitForm = () => {
        const formResponse = generateFormResponse(this.props.fields, this.props._id, this.state.formFieldValues);

        if (this.props.isPrechatCaptureForm) {
            this.setState({ isFormSubmitted: true });

            this.props.submitPrechatCapture(formResponse).catch((err) => {
                if (err && err.message === PRECHAT_CAPTURE_ERROR) {
                    this.setState({ hasFormSubmissionFailed: true, isFormSubmitted: false });
                }
            });
        } else {
            const callbacks = {
                onSuccessCallback: this.onFormSubmitted,
                onErrorCallback: () => {
                    this.setState({ hasFormSubmissionFailed: true });
                },
            };
            this.props.dispatch(sendMessage(formResponse, { callbacks }));
        }
    };

    render() {
        const {
            _clientId,
            _clientSent,
            _id,
            accentColor,
            avatarUrl,
            clickToRetryText,
            clickToRetryFormText,
            dateFnLocale,
            shouldShowAuthor,
            shouldShowAvatar,
            linkColor,
            messageRef,
            messageTimestampFormat,
            name,
            received,
            sendStatus,
            showStatusIndicator,
            tapToRetryText,
            tapToRetryFormText,
            textFallback,
            type,
            unsupportedMessageTypeText,
            role,
            text,
            authorId,
            currentUser,
        } = this.props;

        const {
            hasText,
            hasActions,
            hasFile,
            hasImageFailure,
            hasImage,
            hasCarousel,
            hasForm,
            hasLocation,
            isCurrentUser,
            isMessageRead,
            otherUsersReadTimestamp,
        } = this.state;
        const actions = this.props.actions.filter(({ type }) => !GLOBAL_ACTION_TYPES.includes(type));

        const avatarPlaceHolder = isCurrentUser ? null : <div className="msg-avatar-placeholder" />;

        const actionList = actions.map((action) => {
            return <Action key={action._id} messageId={_id} buttonColor={linkColor} {...action} />;
        });

        const avatar = isCurrentUser ? null : (
            <Avatar messageId={_id} hasImage={hasImage} avatarUrl={avatarUrl || defaultAvatar.icon} />
        );

        let textPart;

        const isSupportedType = isSupportedMessageType(type);

        if (!isSupportedType && textFallback) {
            textPart = <TextMessage {...this.props} text={textFallback} className={this._getTextClasses()} />;
        } else if (!isSupportedType && !textFallback) {
            textPart = (
                <TextMessage {...this.props} text={unsupportedMessageTypeText} className={this._getTextClasses()} />
            );
        } else if (hasText && !shouldIgnoreText(this.props.type)) {
            textPart = <TextMessage {...this.props} className={this._getTextClasses()} />;
        }

        const imagePart = hasImage && (
            <ImageMessage
                accentColor={accentColor}
                mediaSize={this.props.mediaSize}
                mediaUrl={this.props.mediaUrl}
                onError={this.onImageError}
                onLoad={this.onLoadImage}
                sendStatus={sendStatus}
                type={type}
            />
        );

        const filePart = hasFile && <FileMessage {...this.props} className={this._getTextClasses()} />;
        const carouselPart = hasCarousel && <CarouselMessage {...this.props} />;

        const formPart = hasForm && (
            <FormMessage
                className="message-item message-form"
                formFieldValues={this.state.formFieldValues}
                isFormSubmitted={!!this.state.isFormSubmitted}
                setFormFieldValue={this.setFormFieldValue}
                submitForm={this.submitForm}
                {...this.props}
            />
        );

        const locationPart = hasLocation && (
            <LocationMessage
                accentColor={accentColor}
                className={this._getLocationClasses()}
                coordinates={this.props.coordinates}
                dispatch={this.props.dispatch}
                role={role}
                sendStatus={sendStatus}
                text={text}
                type={type}
                authorId={authorId}
                currentUser={currentUser}
            />
        );

        const styles = {
            container: {},
            wrapper: {},
        };

        if (!hasImage || hasImageFailure || hasActions || hasText) {
            if (isCurrentUser && accentColor) {
                styles.container.backgroundColor = styles.container.borderLeftColor = `#${accentColor}`;
            }
        }

        if (hasCarousel && IS_FIREFOX) {
            styles.wrapper.marginBottom = CAROUSEL_MESSAGE.SCROLL_OFFSET;
        }

        const fromName = <div className="from">{isCurrentUser ? '' : name}</div>;

        const clickToRetry = (
            <div className="retry">
                {isMobile().any
                    ? this.props.type === MESSAGE_TYPES.FORM_RESPONSE
                        ? tapToRetryFormText
                        : tapToRetryText
                    : this.props.type === MESSAGE_TYPES.FORM_RESPONSE
                    ? clickToRetryFormText
                    : clickToRetryText}
            </div>
        );

        return (
            <div className={this._getRowClasses()} ref={messageRef}>
                {!isCurrentUser && shouldShowAuthor ? fromName : null}
                <div className="msg-wrapper" style={styles.wrapper}>
                    {shouldShowAvatar ? avatar : avatarPlaceHolder}
                    <div
                        className={this._getContainerClasses()}
                        style={styles.container}
                        ref={(c) => (this._messageContent = c)}
                        onClick={this.onMessageClick.bind(this)}
                        data-tip={formatDate(new Date((_clientSent || received) * 1000), messageTimestampFormat, {
                            locale: dateFnLocale,
                        })}
                        data-for={`tooltip-${_clientId || _id}`}
                        data-iscapture="true"
                    >
                        {formPart}
                        {hasCarousel ? (
                            carouselPart
                        ) : (
                            <div>
                                {imagePart ? imagePart : null}
                                {textPart ? textPart : null}
                                {locationPart ? locationPart : null}
                                {filePart ? filePart : null}
                                {hasActions ? <div className={this._getActionClasses()}>{actionList}</div> : null}
                            </div>
                        )}
                    </div>
                    {!(hasCarousel || hasForm) ? (
                        <Tooltip
                            id={`tooltip-${_clientId || _id}`}
                            effect="solid"
                            place="top"
                            event="mouseenter touchstart"
                            eventOff="mouseleave touchstart"
                            globalEventOff="mouseleave touchstart"
                            insecure={false}
                            delayShow={500}
                        />
                    ) : null}
                </div>
                {sendStatus === SEND_STATUS.FAILED ? clickToRetry : null}
                {sendStatus !== SEND_STATUS.FAILED && showStatusIndicator && (
                    <MessageStatusIndicator
                        value={isMessageRead ? otherUsersReadTimestamp : received}
                        isCurrentUser={isCurrentUser}
                        isMessageRead={isMessageRead}
                    />
                )}
                <div className="clear"></div>
            </div>
        );
    }
}

export default connect(
    ({ appState, conversations, ui: { text, dateFnLocale }, user: currentUser }) => {
        const conversation = conversations[appState.activeConversationId];

        return {
            dateFnLocale,
            clickToRetryText: text.clickToRetry,
            clickToRetryFormText: text.clickToRetryForm,
            isChatInputBlocked: appState.blockChatInput,
            locationSendingFailedText: text.locationSendingFailed,
            messageTimestampFormat: text.messageTimestampFormat,
            tapToRetryText: text.tapToRetry,
            tapToRetryFormText: text.tapToRetryForm,
            unsupportedMessageTypeText: text.unsupportedMessageType,
            currentUser,
            latestAppUserMessageTimestamp: conversation.latestAppUserMessageTimestamp,
            conversationId: appState.activeConversationId,
            appMakerLastRead: conversation.appMakerLastRead,
            participants: conversation.participants,
        };
    },
    undefined,
    undefined,
    {
        forwardRef: true,
    },
)(MessageComponent);
